<script setup lang="ts">
/**
 * 兌換報表（批107 項5）—— 總覽／領用明細／使用明細三個頁籤。
 *
 * **兩個 realm 共用這一支**（我方 `/redeem-reports`、代理商 `/reseller/redeem-reports`），
 * 分流方式與 `RedeemCampaignsView` 一樣由 `auth.isReseller` 決定，但 API 走的是
 * `api/redeemReports.ts`／`api/resellerRedeemReports.ts` 兩個**函式同名**的模組
 * （見 `apiSet()`）。差別只有三處，而且**每一處都是後端決定的**：
 *
 * | | 我方 | 代理商 |
 * |---|---|---|
 * | 代理商排行 | 有 | `stats.resellers === null`（伺服器端就不算） |
 * | 代理商／產品下拉 | 有 | 無（他的活動必然都是自家的、產品也在範圖內） |
 * | 明細的發放人／作廢人／IP | email＋IP | `*_label`（「我方代發」「我方」），無 IP |
 *
 * ★ 前端的隱藏**不是防線**：代理商打我方那三支端點一律 403（router-level
 *   `require_reseller` 的對稱面），排行的資料他連請求都拿不到。
 *
 * ## 兩個語意（畫面上的文案也照這個講）
 *
 * - **領用＝發碼**：期間內「發出去」的子碼，**現在**各是什麼狀態（cohort）。
 *   共用碼不算發碼（整檔活動只有一列碼）。
 * - **使用＝兌換**：期間內發生的每一次兌換（事件）。
 *
 * 兩者不會相等，也不該相等——七月發的碼在八月被兌掉，只會出現在八月的「使用」。
 */
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import DailyCountChart from '@/components/DailyCountChart.vue'
import FilterClearButton from '@/components/FilterClearButton.vue'
import UiButton from '@/components/UiButton.vue'
import { ApiError } from '@/api/client'
import type {
  IssuedQuery,
  IssuedRow,
  RedeemStats,
  RedemptionQuery,
  RedemptionRow,
  ReportQuery,
} from '@/api/redeemReports'
import * as staffReports from '@/api/redeemReports'
import * as resellerReports from '@/api/resellerRedeemReports'
import type { RedeemCampaign } from '@/api/ls'
import { redeemKindLabel } from '@/lib/licenseLabels'
import { useRedeem } from '@/lib/redeemApi'
import { customerOptionLabel } from '@/lib/customerLabel'
import { usePersistedFilters } from '@/lib/persistedFilters'
import { useAuthStore } from '@/stores/auth'
import { useDataStore } from '@/stores/data'
import { useDialogsStore } from '@/stores/dialogs'
import { vCardTable } from '@/lib/cardTable'

const auth = useAuthStore()
const store = useDataStore()
const dialogs = useDialogsStore()
const route = useRoute()
/** 只用它的 `api()`（活動下拉要一份活動清單）——報表自己的三支 API 走 `apiSet()`。 */
const { api: campaignApi } = useRedeem()

const isReseller = computed(() => auth.isReseller)
/**
 * 依角色挑一組**函式同名**的 API 模組。
 *
 * ★ 每次取值才問（不是 setup 期算一次的常數）：角色是 `GET /me` 載回來的，
 *   setup 當下可能還沒回來——同 `lib/redeemApi.ts::useRedeem` 的理由。
 */
function apiSet() {
  return auth.isReseller ? resellerReports : staffReports
}

type Tab = 'overview' | 'issued' | 'redemptions'
/**
 * 日期區間怎麼記（批186）：`''`＝交給後端決定（近 30 天）；`'7'`／`'90'`＝**滾動**的近 N 天（每次進頁以後端的
 * 今天重算）；`'custom'`＝手選的固定起訖（`from`／`to`）。輸入框的 `dateFrom`／`dateTo` 本身不記——
 * 第一次載回來會被後端實際用的區間填上，記了就變成「把三十天前那天釘死」。
 */
type Range = '' | '7' | '90' | 'custom'

// --- 過濾條件 ---------------------------------------------------------------
// 批186：下拉、頁籤與日期區間記在 `lib/persistedFilters`；活動／客戶詳情頁帶 `campaign_id`／
// `customer_id`（／`tab`）連進來時，整組換成網址的條件。
const pf = usePersistedFilters(
  'redeem-reports',
  {
    tab: 'overview' as Tab,
    range: '' as Range,
    from: '',
    to: '',
    productFilter: '',
    resFilter: '',
    campaignFilter: '',
    kindFilter: '',
    customerFilter: '',
    codeStatusFilter: '',
    issuedRealmFilter: '',
    channelFilter: '',
  },
  {
    oneOf: {
      tab: ['overview', 'issued', 'redemptions'],
      range: ['', '7', '90', 'custom'],
      kindFilter: ['', 'extend', 'trial', 'grant'],
      codeStatusFilter: ['', 'issued', 'redeemed', 'void'],
      issuedRealmFilter: ['', 'staff', 'reseller'],
      channelFilter: ['', 'product', 'portal', 'link'],
    },
    deepLink: {
      route,
      router: useRouter(),
      map: { campaign_id: 'campaignFilter', customer_id: 'customerFilter', tab: 'tab' },
    },
  },
)
const { tab, range, productFilter, resFilter, campaignFilter, kindFilter, customerFilter } = pf.refs
/** 只作用在明細（後端也是——套進統計會讓「依通路分佈」只剩被選中的那一格）。 */
const { codeStatusFilter, issuedRealmFilter, channelFilter } = pf.refs
const { dirty: filtersDirty } = pf
/** 空字串＝交給後端決定（近 30 天）；第一次載回來之後填成後端實際用的區間。 */
const dateFrom = ref(range.value === 'custom' ? pf.refs.from.value : '')
const dateTo = ref(range.value === 'custom' ? pf.refs.to.value : '')
/** 後端的「今天」（沒帶迄日時後端回的 `window.to`）——快速區間以它為錨，不用瀏覽器的今天（時區可能不同）。 */
const serverToday = ref<string | null>(null)

function baseQuery(): ReportQuery {
  return {
    from: dateFrom.value || undefined,
    to: dateTo.value || undefined,
    product: isReseller.value ? undefined : productFilter.value || undefined,
    // 代理商那份 API 會在送出前丟掉這個鍵；這裡也不送，免得讀程式的人以為他能挑別家。
    reseller_id: isReseller.value ? undefined : resFilter.value || undefined,
    campaign_id: campaignFilter.value || undefined,
    kind: kindFilter.value || undefined,
    customer_id: customerFilter.value || undefined,
  }
}
function issuedQuery(cursor?: string | null): IssuedQuery {
  return {
    ...baseQuery(),
    status: codeStatusFilter.value || undefined,
    issued_realm: issuedRealmFilter.value || undefined,
    cursor: cursor || undefined,
  }
}
function redemptionQuery(cursor?: string | null): RedemptionQuery {
  return {
    ...baseQuery(),
    channel: channelFilter.value || undefined,
    cursor: cursor || undefined,
  }
}

// --- 載入 -------------------------------------------------------------------

const stats = ref<RedeemStats | null>(null)
const statsLoading = ref(true)
const loadError = ref('')

const issuedRows = ref<IssuedRow[]>([])
const issuedCursor = ref<string | null>(null)
const issuedLoading = ref(false)
const issuedLoaded = ref(false)

const redRows = ref<RedemptionRow[]>([])
const redCursor = ref<string | null>(null)
const redLoading = ref(false)
const redLoaded = ref(false)

const exporting = ref('')

function errText(e: unknown, fallback: string): string {
  const d =
    e instanceof ApiError && e.payload ? (e.payload as { detail?: unknown }).detail : undefined
  if (d == null) return fallback
  if (typeof d === 'string') return d
  const obj = d as { message?: string; code?: string }
  return obj.message ?? obj.code ?? JSON.stringify(d)
}
async function fail(e: unknown, fallback: string) {
  await dialogs.notify({ title: '操作失敗', message: errText(e, fallback) })
}

async function loadStats() {
  statsLoading.value = true
  try {
    const q = baseQuery()
    const body = await apiSet().reportStats(q)
    stats.value = body
    if (!q.to) serverToday.value = body.window.to
    // 第一次載回來時把「後端實際用的區間」填進輸入框——不填的話使用者看到兩個空格子，
    // 卻不知道畫面上這些數字是哪一段期間的。
    if (!dateFrom.value) dateFrom.value = body.window.from
    if (!dateTo.value) dateTo.value = body.window.to
    loadError.value = ''
  } catch (e) {
    loadError.value = errText(e, '讀取兌換報表失敗')
  } finally {
    statsLoading.value = false
  }
}

async function loadIssued(cursor: string | null) {
  issuedLoading.value = true
  try {
    const page = await apiSet().reportIssued(issuedQuery(cursor))
    issuedRows.value = cursor ? [...issuedRows.value, ...page.items] : page.items
    issuedCursor.value = page.next_cursor
    issuedLoaded.value = true
  } catch (e) {
    await fail(e, '讀取領用明細失敗')
  } finally {
    issuedLoading.value = false
  }
}

async function loadRedemptions(cursor: string | null) {
  redLoading.value = true
  try {
    const page = await apiSet().reportRedemptions(redemptionQuery(cursor))
    redRows.value = cursor ? [...redRows.value, ...page.items] : page.items
    redCursor.value = page.next_cursor
    redLoaded.value = true
  } catch (e) {
    await fail(e, '讀取使用明細失敗')
  } finally {
    redLoading.value = false
  }
}

/** 改過濾條件＝三份資料全部作廢（明細留著會與總覽對不起來，那比慢一點糟得多）。 */
async function applyFilters() {
  issuedLoaded.value = false
  redLoaded.value = false
  issuedRows.value = []
  redRows.value = []
  await loadStats()
  if (tab.value === 'issued') await loadIssued(null)
  if (tab.value === 'redemptions') await loadRedemptions(null)
}

/** 頁籤懶載：切過去才打那一支（三份資料各自獨立）。 */
watch(tab, async (t) => {
  if (t === 'issued' && !issuedLoaded.value) await loadIssued(null)
  if (t === 'redemptions' && !redLoaded.value) await loadRedemptions(null)
})

// --- 活動下拉（兩個 realm 都要，走 `lib/redeemApi` 的分流）---------------------
const campaigns = ref<RedeemCampaign[]>([])

onMounted(async () => {
  store.ensureProducts().catch(() => {})
  store.ensureCustomers().catch(() => {})
  // ★ admin 打 `/resellers` 會 403，`ensureResellers()` 自己吞掉並標 `resellersDenied`
  //   → 下拉就不畫（不是畫一個永遠空的下拉）。代理商完全不需要這一欄。
  if (!isReseller.value) store.ensureResellers().catch(() => {})
  campaignApi()
    .list({ limit: 200 })
    .then((page) => {
      campaigns.value = page.items
    })
    .catch(() => {})
  // 深連結（活動詳情帶 `campaign_id`、客戶詳情帶 `customer_id`）在 setup 已由 `usePersistedFilters` 套用。
  await loadStats()
  // 記住的是滾動區間：上面那次是後端預設區間（順便拿到後端的今天），以它為錨重算再載一次。
  if (range.value === '7' || range.value === '90') {
    setRollingDates(Number(range.value))
    if (dateFrom.value) await loadStats()
  }
  if (tab.value === 'issued') await loadIssued(null)
  if (tab.value === 'redemptions') await loadRedemptions(null)
})

/** 以後端的今天為迄日、往前推 N 天（含今天）。還沒拿到後端的今天就什麼都不做。 */
function setRollingDates(days: number) {
  const anchor = serverToday.value
  if (!anchor) return
  const end = new Date(`${anchor}T00:00:00Z`)
  dateFrom.value = new Date(end.getTime() - (days - 1) * 86400_000).toISOString().slice(0, 10)
  dateTo.value = anchor
}

/**
 * 快速區間：以**後端回的迄日**為錨（不是瀏覽器的今天——時區可能不同）。
 * 錨是後端的今天＝滾動區間（近 30 天就是後端預設，記成 `''`）；錨是手選的迄日（先手選、再按快速鈕）
 * ＝那一段固定區間，記成自選。
 */
function quickRange(days: number) {
  const anchor = serverToday.value ?? stats.value?.window.to ?? dateTo.value
  if (!anchor) return
  const end = new Date(`${anchor}T00:00:00Z`)
  dateFrom.value = new Date(end.getTime() - (days - 1) * 86400_000).toISOString().slice(0, 10)
  dateTo.value = anchor
  rememberRange(anchor !== serverToday.value ? 'custom' : days === 30 ? '' : (String(days) as Range))
  applyFilters()
}

/** 手動改了起訖：兩格都清空＝回到後端預設，否則記成自選的固定區間。 */
function onDateChange() {
  rememberRange(!dateFrom.value && !dateTo.value ? '' : 'custom')
  applyFilters()
}
/** 只有自選區間才記起訖；其餘清掉，免得殘留的舊日期讓「清除」鈕亮著。 */
function rememberRange(r: Range) {
  range.value = r
  pf.refs.from.value = r === 'custom' ? dateFrom.value : ''
  pf.refs.to.value = r === 'custom' ? dateTo.value : ''
}

function clearFilters() {
  pf.clear()
  dateFrom.value = ''
  dateTo.value = ''
  void applyFilters()
}

// --- CSV --------------------------------------------------------------------

/**
 * CSV 以 blob 下載：端點要 `Authorization` 標頭，`<a href>` 的導覽請求帶不了它
 * （token 在 localStorage，不是 cookie）。
 *
 * 被後端上限截斷時回應帶 `X-Export-Truncated: 1` —— **一定要提示**，否則使用者拿到
 * 一份「看起來完整」的殘缺報表還拿去對帳。
 */
async function exportCsv(kind: 'issued' | 'redemptions') {
  exporting.value = kind
  try {
    const res =
      kind === 'issued'
        ? await apiSet().reportIssuedCsv(issuedQuery())
        : await apiSet().reportRedemptionsCsv(redemptionQuery())
    const blob = await res.blob()
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `redeem-${kind}-${dateFrom.value}_${dateTo.value}.csv`
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
    if (res.headers.get('X-Export-Truncated') === '1') {
      await dialogs.notify({
        title: '匯出已被截斷',
        message:
          '這次的資料超過單檔上限（5 萬列），檔案只包含最新的那 5 萬列。\n請把期間切短再匯一次，否則統計會少算。',
      })
    }
  } catch (e) {
    await fail(e, '匯出失敗')
  } finally {
    exporting.value = ''
  }
}

// --- 顯示 -------------------------------------------------------------------

function fmtDateTime(iso: string | null): string {
  return iso ? iso.replace('T', ' ').slice(0, 16) : '—'
}
function pct(rate: number | null): string {
  return rate == null ? '—' : `${(rate * 100).toFixed(1)}%`
}
function codeStatusLabel(s: string): string {
  return { issued: '未兌換', redeemed: '已兌換', void: '已作廢' }[s] ?? s
}
function codeStatusClass(s: string): string {
  return { issued: 'pending', redeemed: 'active', void: 'revoked' }[s] ?? 'pending'
}
function realmLabel(r: string | null): string {
  return { staff: '我方', reseller: '代理商' }[r ?? ''] ?? '—'
}
function channelLabel(c: string): string {
  return { product: '產品內', portal: '客戶自助', link: '連結領取' }[c] ?? c
}
function ownerText(id: string | null): string {
  if (!id) return '直營'
  return store.resellersDenied ? '代理商專屬' : store.resellerName(id)
}

const channelTotal = computed(() =>
  (stats.value?.channels ?? []).reduce((s, c) => s + c.count, 0),
)
const showResellerPicker = computed(
  () => !isReseller.value && !store.resellersDenied && store.resellers.length > 0,
)
</script>

<template>
  <div class="page">
    <div class="page-head">
      <h1>兌換報表</h1>
      <div class="head-acts">
        <span v-if="stats" class="subtle tz">時區 {{ stats.window.timezone }}</span>
      </div>
    </div>

    <!-- ===== 過濾列（三個頁籤共用；後兩項只作用在明細）===== -->
    <div class="card filters">
      <div class="f-row">
        <label class="f">
          <span class="f-label">起</span>
          <input v-model="dateFrom" type="date" @change="onDateChange()" />
        </label>
        <label class="f">
          <span class="f-label">迄</span>
          <input v-model="dateTo" type="date" @change="onDateChange()" />
        </label>
        <div class="quick">
          <button type="button" class="chip pick" @click="quickRange(7)">近 7 天</button>
          <button type="button" class="chip pick" @click="quickRange(30)">近 30 天</button>
          <button type="button" class="chip pick" @click="quickRange(90)">近 90 天</button>
        </div>
        <span class="f-spacer" />
        <FilterClearButton :dirty="filtersDirty" @click="clearFilters" />
      </div>

      <div class="f-row">
        <select v-model="campaignFilter" class="f-sel" @change="applyFilters()">
          <option value="">全部活動</option>
          <option v-for="c in campaigns" :key="c.id" :value="c.id">{{ c.name }}</option>
        </select>
        <select v-model="kindFilter" class="f-sel" @change="applyFilters()">
          <option value="">全部類型</option>
          <option value="extend">延長期限</option>
          <option value="trial">功能試用</option>
          <option value="grant">領取試用授權</option>
        </select>
        <select v-if="showResellerPicker" v-model="resFilter" class="f-sel" @change="applyFilters()">
          <option value="">全部歸屬</option>
          <option value="none">我方直營</option>
          <option v-for="r in store.resellers" :key="r.id" :value="r.id">{{ r.name }}</option>
        </select>
        <select v-if="!isReseller" v-model="productFilter" class="f-sel" @change="applyFilters()">
          <option value="">全部產品</option>
          <option v-for="p in store.products" :key="p.key" :value="p.key">{{ p.name }}</option>
        </select>
        <select v-model="customerFilter" class="f-sel" @change="applyFilters()">
          <option value="">全部客戶</option>
          <option v-for="c in store.customers" :key="c.id" :value="c.id">{{ customerOptionLabel(c, { ownership: !auth.isReseller }) }}</option>
        </select>
        <select
          v-if="tab === 'issued'"
          v-model="codeStatusFilter"
          class="f-sel"
          @change="applyFilters()"
        >
          <option value="">全部代碼狀態</option>
          <option value="issued">未兌換</option>
          <option value="redeemed">已兌換</option>
          <option value="void">已作廢</option>
        </select>
        <select
          v-if="tab === 'issued'"
          v-model="issuedRealmFilter"
          class="f-sel"
          @change="applyFilters()"
        >
          <option value="">全部發放來源</option>
          <option value="staff">我方</option>
          <option value="reseller">代理商</option>
        </select>
        <select
          v-if="tab === 'redemptions'"
          v-model="channelFilter"
          class="f-sel"
          @change="applyFilters()"
        >
          <option value="">全部兌換通路</option>
          <option value="product">產品內</option>
          <option value="portal">客戶自助</option>
          <option value="link">連結領取</option>
        </select>
      </div>
    </div>

    <p v-if="loadError" class="err-line">{{ loadError }}</p>

    <div class="tabs" data-hscroll>
      <button class="tab" :class="{ active: tab === 'overview' }" @click="tab = 'overview'">
        總覽
      </button>
      <button class="tab" :class="{ active: tab === 'issued' }" @click="tab = 'issued'">
        領用明細
      </button>
      <button class="tab" :class="{ active: tab === 'redemptions' }" @click="tab = 'redemptions'">
        使用明細
      </button>
    </div>

    <!-- ===== 總覽 ===== -->
    <div v-show="tab === 'overview'">
      <div v-if="statsLoading" class="loading-wrap"><span class="spinner lg" /></div>
      <template v-else-if="stats">
        <p v-if="stats.campaigns_truncated" class="warn-line">
          符合條件的活動超過 200 檔，以下統計只涵蓋最新的 200 檔。請縮小期間或加上活動／產品條件。
        </p>

        <div class="kpis">
          <div class="kpi">
            <div class="kpi-label">發碼</div>
            <div class="kpi-value">{{ stats.kpi.issued }}</div>
            <div class="kpi-sub">
              已兌 {{ stats.kpi.issued_redeemed }}・未兌 {{ stats.kpi.issued_unused }}・作廢
              {{ stats.kpi.issued_voided }}
            </div>
          </div>
          <div class="kpi">
            <div class="kpi-label">兌換</div>
            <div class="kpi-value">{{ stats.kpi.redeemed }}</div>
            <div class="kpi-sub">期間內發生的兌換次數</div>
          </div>
          <div class="kpi">
            <div class="kpi-label">兌換客戶</div>
            <div class="kpi-value">{{ stats.kpi.customers }}</div>
            <div class="kpi-sub">期間內兌換過的不重複客戶</div>
          </div>
        </div>

        <div class="card">
          <div class="card-head"><h3 class="section-title">每日趨勢</h3></div>
          <DailyCountChart :points="stats.daily" />
          <p class="subtle note">
            <strong>發碼</strong>依代碼發出的日期分組（共用碼不計）；<strong>兌換</strong>依兌換發生的日期分組。
            兩條線不會相等——上個月發的碼可能這個月才被兌掉。
          </p>
        </div>

        <div class="card">
          <div class="card-head"><h3 class="section-title">各活動</h3></div>
          <div class="tbl-scroll" data-hscroll>
            <table v-card-table class="tbl card-table">
              <thead>
                <tr>
                  <th>活動</th>
                  <th>類型</th>
                  <th v-if="!isReseller">產品</th>
                  <th v-if="!isReseller">歸屬</th>
                  <th>期間發碼</th>
                  <th>已兌 / 未兌 / 作廢</th>
                  <th>期間兌換</th>
                  <th>兌換率</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="c in stats.campaigns" :key="c.campaign_id">
                  <td>
                    <RouterLink
                      class="lnk"
                      :to="{
                        name: isReseller ? 'reseller-redeem-campaign-detail' : 'redeem-campaign-detail',
                        params: { id: c.campaign_id },
                      }"
                    >
                      {{ c.name }}
                    </RouterLink>
                    <div class="sub">{{ c.code_mode === 'shared' ? '共用碼' : '不重複子碼' }}</div>
                  </td>
                  <td class="small">{{ redeemKindLabel(c.kind) }}</td>
                  <td v-if="!isReseller" class="small">{{ store.productName(c.product) }}</td>
                  <td v-if="!isReseller" class="small">{{ ownerText(c.reseller_id ?? null) }}</td>
                  <!-- 共用碼沒有「發碼」這件事（整檔活動只有一列碼）→ 這兩欄一律「—」，
                       不是 0：0 會被讀成「發了但沒人領」，而實際上是「不適用」。 -->
                  <td class="small num">{{ c.code_mode === 'shared' ? '—' : c.issued }}</td>
                  <td class="small num">
                    <template v-if="c.code_mode === 'shared'">—</template>
                    <template v-else>
                      {{ c.issued_redeemed }} / {{ c.issued_unused }} / {{ c.issued_voided }}
                    </template>
                  </td>
                  <td class="small num">{{ c.redeemed }}</td>
                  <td class="small num">{{ pct(c.redeem_rate) }}</td>
                </tr>
                <tr v-if="!stats.campaigns.length">
                  <td :colspan="isReseller ? 6 : 8" class="subtle empty-row">
                    這個期間沒有符合條件的兌換活動。
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <p class="subtle note">
            兌換率：不重複子碼＝<strong>期間內已兌換 ÷ 期間內發出</strong>；共用碼＝<strong>已兌換 ÷ 名額</strong>
            （不限量就沒有母數，顯示「—」）。
          </p>
        </div>

        <!-- 代理商視角沒有排行那一塊 → 整列收成一欄，否則「依兌換通路」會只佔半寬、
             空狀態的一行字被擠成兩行。 -->
        <div class="two-col" :class="{ single: !stats.resellers }">
          <div class="card">
            <div class="card-head"><h3 class="section-title">依兌換通路</h3></div>
            <ul class="bars">
              <li v-for="c in stats.channels" :key="c.channel">
                <span class="bar-label">{{ channelLabel(c.channel) }}</span>
                <span class="bar-track">
                  <span
                    class="bar-fill"
                    :style="{ width: `${channelTotal ? (c.count / channelTotal) * 100 : 0}%` }"
                  />
                </span>
                <span class="bar-value">{{ c.count }}</span>
              </li>
              <!-- 空狀態要脫離 `grid-template-columns`（否則整句會被塞進 84px 的標籤欄裡）。 -->
              <li v-if="!stats.channels.length" class="subtle bars-empty">
                這個期間沒有兌換紀錄。
              </li>
            </ul>
          </div>

          <!-- ★ 代理商排行只有我方看得到：後端在 `scope.is_reseller` 時根本不算，
               這裡的 `v-if` 只是不畫一塊永遠是 null 的區塊。 -->
          <div v-if="stats.resellers" class="card">
            <div class="card-head"><h3 class="section-title">代理商排行</h3></div>
            <div class="tbl-scroll" data-hscroll>
              <table v-card-table class="tbl card-table">
                <thead>
                  <tr>
                    <th>歸屬</th>
                    <th>兌換</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="r in stats.resellers" :key="r.reseller_id ?? 'direct'">
                    <td class="small">{{ r.reseller_name ?? '我方直營' }}</td>
                    <td class="small num">{{ r.redeemed }}</td>
                  </tr>
                  <tr v-if="!stats.resellers.length">
                    <td colspan="2" class="subtle empty-row">這個期間沒有兌換紀錄。</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </template>
    </div>

    <!-- ===== 領用明細 ===== -->
    <div v-show="tab === 'issued'" class="card">
      <div class="card-head">
        <h3 class="section-title">領用明細（發碼）</h3>
        <span class="spacer" />
        <UiButton size="sm" :loading="exporting === 'issued'" @click="exportCsv('issued')">
          匯出 CSV
        </UiButton>
      </div>
      <p class="subtle note">
        期間內<strong>發出</strong>的不重複子碼，以及它們<strong>現在</strong>的狀態。
        共用碼不列（整檔活動只有一組碼，不是發出去的）。代碼一律遮罩，完整代碼請到活動詳情頁。
      </p>
      <div class="tbl-scroll" data-hscroll>
        <table v-card-table class="tbl card-table">
          <thead>
            <tr>
              <th>發放時間</th>
              <th>活動</th>
              <th>代碼</th>
              <th>狀態</th>
              <th>發給客戶</th>
              <th>發放來源</th>
              <th>兌換時間</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="r in issuedRows" :key="r.id">
              <td class="small">{{ fmtDateTime(r.issued_at) }}</td>
              <td class="small">{{ r.campaign_name }}</td>
              <td class="small mono">{{ r.masked_code }}</td>
              <td>
                <span class="status-pill" :class="codeStatusClass(r.status)">
                  {{ codeStatusLabel(r.status) }}
                </span>
                <div v-if="r.voided_at" class="sub">
                  {{ fmtDateTime(r.voided_at) }}
                  {{ isReseller ? (r.voided_by_label ?? '') : (r.voided_by ?? '') }}
                </div>
              </td>
              <td class="small">
                {{ r.distributed_to_customer_name ?? '—' }}
                <div v-if="r.distributed_note" class="sub">{{ r.distributed_note }}</div>
              </td>
              <td class="small">
                {{ isReseller ? (r.issued_by_label ?? '—') : (r.issued_by ?? '—') }}
                <div class="sub">{{ realmLabel(r.issued_realm) }}</div>
              </td>
              <td class="small">{{ fmtDateTime(r.redeemed_at) }}</td>
            </tr>
            <tr v-if="!issuedRows.length && !issuedLoading">
              <td colspan="7" class="subtle empty-row">這個期間沒有符合條件的發碼紀錄。</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-if="issuedCursor" class="more">
        <UiButton size="sm" :loading="issuedLoading" @click="loadIssued(issuedCursor)">
          載入更多
        </UiButton>
        <span class="subtle">已載入 {{ issuedRows.length }} 筆</span>
      </div>
    </div>

    <!-- ===== 使用明細 ===== -->
    <div v-show="tab === 'redemptions'" class="card">
      <div class="card-head">
        <h3 class="section-title">使用明細（兌換）</h3>
        <span class="spacer" />
        <UiButton
          size="sm"
          :loading="exporting === 'redemptions'"
          @click="exportCsv('redemptions')"
        >
          匯出 CSV
        </UiButton>
      </div>
      <p class="subtle note">期間內發生的每一次兌換。代碼一律遮罩。</p>
      <div class="tbl-scroll" data-hscroll>
        <table v-card-table class="tbl card-table">
          <thead>
            <tr>
              <th>兌換時間</th>
              <th>活動</th>
              <th>代碼</th>
              <th>通路</th>
              <th>客戶</th>
              <th>部署</th>
              <th>兌換人</th>
              <th v-if="!isReseller">來源 IP</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="r in redRows" :key="r.id">
              <td class="small">{{ fmtDateTime(r.redeemed_at) }}</td>
              <td class="small">
                {{ r.campaign_name ?? '—' }}
                <div class="sub">{{ redeemKindLabel(r.kind) }}</div>
              </td>
              <td class="small mono">{{ r.masked_code }}</td>
              <td class="small">{{ channelLabel(r.channel) }}</td>
              <td class="small">{{ r.customer_name ?? '—' }}</td>
              <td class="small">
                {{ r.hostname ?? '—' }}
                <div class="sub mono">{{ r.instance_id.slice(0, 8) }}</div>
              </td>
              <td class="small">
                {{ r.actor_email ?? '—' }}
                <div v-if="!r.actor_verified" class="sub">未驗證（產品端自報）</div>
              </td>
              <td v-if="!isReseller" class="small mono">{{ r.client_ip ?? '—' }}</td>
            </tr>
            <tr v-if="!redRows.length && !redLoading">
              <td :colspan="isReseller ? 7 : 8" class="subtle empty-row">
                這個期間沒有符合條件的兌換紀錄。
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-if="redCursor" class="more">
        <UiButton size="sm" :loading="redLoading" @click="loadRedemptions(redCursor)">
          載入更多
        </UiButton>
        <span class="subtle">已載入 {{ redRows.length }} 筆</span>
      </div>
    </div>
  </div>
</template>

<style scoped>
.page-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--sp-3);
  margin-bottom: var(--sp-4);
}
.page-head h1 {
  margin: 0;
  font-size: var(--fs-20);
  color: var(--fg);
}
.head-acts {
  display: flex;
  align-items: center;
  gap: var(--sp-2);
}
.tz {
  font-size: var(--fs-11);
}
.filters {
  display: flex;
  flex-direction: column;
  gap: var(--sp-2);
  margin-bottom: var(--sp-3);
}
.f-row {
  display: flex;
  align-items: center;
  gap: var(--sp-2);
  flex-wrap: wrap;
}
.f {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.f-label {
  font-size: var(--fs-12);
  color: var(--fg-3);
}
.f-sel {
  min-width: 132px;
}
.quick {
  display: inline-flex;
  gap: 5px;
  margin-left: var(--sp-2);
}
/* 把「清除全部條件」推到第一列最右邊（換行時自然落到下一行開頭）。 */
.f-spacer {
  flex: 1;
}
.chip.pick {
  cursor: pointer;
  border: 1px solid var(--border-strong);
  background: transparent;
  color: var(--fg-3);
  height: 24px;
}
.chip.pick:hover {
  border-color: var(--brand-400);
  color: var(--brand-700);
}
.tabs {
  margin-bottom: var(--sp-3);
}
.err-line {
  margin: 0 0 var(--sp-3);
  color: var(--danger);
  font-size: var(--fs-12);
}
.warn-line {
  margin: 0 0 var(--sp-3);
  color: var(--warning);
  font-size: var(--fs-12);
}
.kpis {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: var(--sp-3);
  margin-bottom: var(--sp-3);
}
.kpi {
  padding: var(--sp-3) var(--sp-4);
  background: var(--bg-panel);
  border: 1px solid var(--border);
  border-radius: var(--r-md);
}
.kpi-label {
  font-size: var(--fs-12);
  color: var(--fg-3);
}
.kpi-value {
  margin: 2px 0;
  font-size: var(--fs-24, 24px);
  font-weight: 700;
  color: var(--fg);
  font-variant-numeric: tabular-nums;
}
.kpi-sub {
  font-size: var(--fs-11);
  color: var(--fg-4);
}
.two-col {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--sp-3);
  align-items: start;
}
.two-col.single {
  grid-template-columns: 1fr;
}
.two-col > .card {
  margin: 0;
}
.note {
  margin: var(--sp-2) 0 0;
  font-size: var(--fs-11);
  line-height: var(--lh-base);
}
.bars {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.bars li {
  display: grid;
  grid-template-columns: 84px 1fr 44px;
  align-items: center;
  gap: var(--sp-2);
  font-size: var(--fs-12);
  color: var(--fg-2);
}
.bars li.bars-empty {
  display: block;
}
.bar-track {
  height: 8px;
  border-radius: 4px;
  background: var(--bg-hover);
  overflow: hidden;
}
.bar-fill {
  display: block;
  height: 100%;
  background: var(--brand-500);
}
.bar-value {
  text-align: right;
  font-variant-numeric: tabular-nums;
  color: var(--fg);
  font-weight: 600;
}
.lnk {
  color: var(--brand-600);
  text-decoration: none;
  font-weight: 600;
}
.lnk:hover {
  text-decoration: underline;
}
.sub {
  color: var(--fg-4);
  font-size: var(--fs-11);
  margin-top: 2px;
}
.small {
  font-size: var(--fs-12);
}
.num {
  text-align: right;
  font-variant-numeric: tabular-nums;
}
.empty-row {
  text-align: center;
  padding: var(--sp-5) 0;
}
.mono {
  font-family: ui-monospace, monospace;
}
.more {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--sp-2);
  padding: var(--sp-3);
  border-top: 1px solid var(--border);
}
@media (max-width: 900px) {
  .two-col {
    grid-template-columns: 1fr;
  }
}
@media (max-width: 700px) {
  .page-head {
    flex-direction: column;
    align-items: stretch;
  }
  .quick {
    margin-left: 0;
  }
}
</style>
